import {Helmet} from '@dr.pogodin/react-helmet';
import {
  ButtonGroup,
  DataStatus,
  Dialog,
  DialogFooter,
  Form,
  FormButton,
  FormField,
  FormSubmitHandler,
  showNotification,
} from 'platform/components';
import {Box} from 'platform/foundation';

import {isNotNilOrEmpty} from 'ramda-adjunct';

import {
  useGetDirectSaleVariantsQuery,
  usePatchDirectSaleMutation,
  usePostDirectSaleMutation,
} from '@dms/api/warehouse';
import {i18n} from '@dms/i18n';
import {testIds, warehouseRoutes} from '@dms/routes';
import {handleApiError, queryParams} from '@dms/shared';
import {getOptionsFromActiveDirectSaleVariants} from '@dms/warehouse/shared';

import {composePath, useNavigate, useQueryState} from 'shared/web';

import {DirectSaleCreateForm, DirectSaleCreateFormSchema} from '../../types/DirectSaleCreateForm';

export function DirectSalesCreate() {
  const navigate = useNavigate();

  const [customerId] = useQueryState(queryParams.CUSTOMER_ID);

  const {
    data: directSaleVariants,
    isLoading: isDirectSaleVariantsLoading,
    isError: hasDirectSaleVariantsError,
  } = useGetDirectSaleVariantsQuery();

  const [createDirectSale] = usePostDirectSaleMutation();
  const [patchDirectSale] = usePatchDirectSaleMutation();

  const navigateToDirectSaleDetail = (id: string) => {
    showNotification.success(i18n.t('entity.warehouse.notifications.directSaleCreated'));

    navigate(
      composePath(warehouseRoutes.directSalesDetailOverview, {
        params: {
          id,
        },
      })
    );
  };

  const handleSubmit: FormSubmitHandler<DirectSaleCreateForm> = async (formValues) => {
    await createDirectSale(formValues)
      .unwrap()
      .then((response) => {
        if (isNotNilOrEmpty(response.id)) {
          // Used for creating direct sale from customer detail
          if (isNotNilOrEmpty(customerId)) {
            return patchDirectSale({directSaleId: response.id, body: {customerId}})
              .unwrap()
              .then(() => navigateToDirectSaleDetail(response.id));
          }

          navigateToDirectSaleDetail(response.id);
        }
      })
      .catch(handleApiError);
  };

  const handleDiscard = () => {
    navigate(warehouseRoutes.directSalesList);
  };

  return (
    <>
      <Helmet title={i18n.t('page.warehouse.labels.newDirectSale')} />
      <DataStatus
        minHeight={60}
        isLoading={isDirectSaleVariantsLoading}
        isError={hasDirectSaleVariantsError}
      >
        <Dialog
          isOpen
          title={i18n.t('page.warehouse.labels.newDirectSale')}
          size="small"
          withAdditionalFooter
          scrollBehavior="outside"
          onClose={handleDiscard}
          data-testid={testIds.warehouse.directSalesCreate('createDialog')}
        >
          <Form<DirectSaleCreateForm>
            experimentalZodSchema={DirectSaleCreateFormSchema}
            onSubmit={handleSubmit}
          >
            {(control) => (
              <>
                <Box height={20}>
                  <FormField
                    isRequired
                    isNotClearable
                    control={control}
                    name="directSaleVariantId"
                    type="choice"
                    label={i18n.t('page.warehouse.labels.directSaleVariant')}
                    options={getOptionsFromActiveDirectSaleVariants(directSaleVariants)}
                    data-testid={testIds.warehouse.directSalesCreate('directSaleVariantId')}
                  />
                </Box>

                <DialogFooter>
                  <ButtonGroup align="right">
                    <FormButton
                      type="button"
                      title={i18n.t('general.actions.discard')}
                      variant="secondary"
                      control={control}
                      onClick={handleDiscard}
                      data-testid={testIds.warehouse.directSalesCreate('discard')}
                    />
                    <FormButton
                      type="submit"
                      title={i18n.t('general.actions.create')}
                      variant="primary"
                      control={control}
                      data-testid={testIds.warehouse.directSalesCreate('submit')}
                    />
                  </ButtonGroup>
                </DialogFooter>
              </>
            )}
          </Form>
        </Dialog>
      </DataStatus>
    </>
  );
}
